import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

// Native scrollbar styling hits a real cross-browser ceiling (modern Chrome
// ignores ::-webkit-scrollbar sizing once scrollbar-color/-width are set),
// so the visible bar is a custom track/thumb pair synced to the carousel's
// actual scroll position here instead of relying on the native scrollbar.
function syncThumb(scroller, thumb) {
  const thumbRatio = scroller.clientWidth / scroller.scrollWidth;
  const maxScroll = scroller.scrollWidth - scroller.clientWidth;
  const scrollRatio = maxScroll > 0 ? scroller.scrollLeft / maxScroll : 0;
  const thumbPercent = thumbRatio * 100;
  thumb.style.width = thumbPercent + '%';
  thumb.style.marginInlineStart = scrollRatio * (100 - thumbPercent) + '%';
}

function updateScrollbarVisibility(section) {
  const scroller = section.querySelector('.resource-list__carousel slideshow-slides');
  const track = section.querySelector('[data-3dids-scrollbar-track]');
  const thumb = section.querySelector('[data-3dids-scrollbar-thumb]');

  if (!scroller || !track || !thumb) return;

  const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 1;
  track.classList.toggle('recently-viewed__scrollbar-track--visible', hasOverflow);

  if (!hasOverflow) return;

  syncThumb(scroller, thumb);

  if (!scroller.dataset.scrollbarBound) {
    scroller.dataset.scrollbarBound = 'true';
    scroller.addEventListener('scroll', () => syncThumb(scroller, thumb));
  }
}

/**
 * Keeps the outer carousel arrows vertically centered on the product image
 * instead of on the complete card (image, title, price and tags).
 * @param {HTMLElement} section
 */
function updateArrowPosition(section) {
  const carousel = section.querySelector('.resource-list__carousel slideshow-container');
  const gallery = section.querySelector('.resource-list__slide .card-gallery');

  if (!carousel || !gallery) return;

  const carouselRect = carousel.getBoundingClientRect();
  const galleryRect = gallery.getBoundingClientRect();

  carousel.style.setProperty('--recently-viewed-media-offset', `${galleryRect.top - carouselRect.top}px`);
  carousel.style.setProperty('--recently-viewed-media-height', `${galleryRect.height}px`);
}

function updateCarouselUi(section) {
  updateScrollbarVisibility(section);
  updateArrowPosition(section);
}

/**
 * Fetches and morphs in the recently viewed products for a single section instance.
 * @param {HTMLElement} root
 */
async function loadRecentlyViewed(root) {
  try {
    const sectionId = root.dataset.sectionId;
    if (!sectionId || root.dataset.recentlyViewedLoaded) return;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    root.dataset.recentlyViewedLoaded = 'true';

    await sectionRenderer.renderSection(sectionId, { url, cache: false });
    updateCarouselUi(root);
  } catch (error) {
    delete root.dataset.recentlyViewedLoaded;
    console.error('[3dids-recently-viewed]', error);
  }
}

function init(scope) {
  (scope || document).querySelectorAll('[data-3dids-recently-viewed]').forEach(loadRecentlyViewed);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// Belt-and-braces: also run shortly after the load event in case something
// on the page delayed or swallowed the earlier attempt. loadRecentlyViewed
// is idempotent (it bails out once root.dataset.recentlyViewedLoaded is set),
// so calling init() again here is a no-op if the first run already succeeded.
window.addEventListener('load', () => {
  setTimeout(() => init(), 500);
});

window.addEventListener('resize', () => {
  document.querySelectorAll('[data-3dids-recently-viewed]').forEach(updateCarouselUi);
});

// The Shopify theme editor re-renders sections via AJAX (live preview) without
// a full page reload, so DOMContentLoaded only fires once. Re-run on the
// editor's section lifecycle events to refetch/re-bind every time this
// section is (re)added or re-rendered.
document.addEventListener('shopify:section:load', (event) => {
  init(event.target);
});

// Back/forward navigation restored from the browser's bfcache reuses the
// existing document instead of firing DOMContentLoaded, so a page you
// bfcache-navigate back to would otherwise be stuck showing whatever it
// looked like (hidden or stale) at the moment it was cached. `pageshow`
// with `event.persisted` fires in that case — re-check localStorage and
// re-fetch so newly viewed products since caching still show up.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  document.querySelectorAll('[data-3dids-recently-viewed]').forEach((root) => {
    delete root.dataset.recentlyViewedLoaded;
    loadRecentlyViewed(root);
  });
});
